<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import TemplateLibrary from '@/components/Conversations/Templates/TemplateLibrary.vue';
import TemplateService from '@/core/conversations/template/template.service';
import type { MessageTemplate } from '@/core/conversations/template/template.type';

/**
 * Conversations shell around the shared TemplateLibrary. Insert still publishes through
 * the persisted `selectedTemplate` store, which MessageCompose watches; the library
 * itself only emits, so other hosts do not touch that store.
 */
const emit = defineEmits(['sendTemplateMessage']);
const templateService = new TemplateService();

const isScreenSmall = ref(window.innerWidth < 960);
const templateLibrary = ref<InstanceType<typeof TemplateLibrary> | null>(null);

const shouldShowTemplatesModal = computed(() => templateService.showTemplateModal);

function closeTemplatesModal() {
  templateLibrary.value?.showBrowse();
  templateService.showTemplateModal = false;
}

function selectTemplate(template: MessageTemplate) {
  templateService.selectedTemplate = template;
  templateService.showTemplateModal = false;
}

function sendTemplate(personalizedMessage: string) {
  emit('sendTemplateMessage', personalizedMessage);
  templateService.showTemplateModal = false;
}

const handleWindowSizeChange = () => {
  isScreenSmall.value = window.innerWidth < 960;
};

onMounted(() => {
  window.addEventListener('resize', handleWindowSizeChange);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleWindowSizeChange);
});
</script>

<template>
  <v-dialog
    :class="[isScreenSmall ? 'w-[600px]' : 'w-[960px]']"
    :model-value="shouldShowTemplatesModal"
    persistent
  >
    <div class="modal-content" :class="[isScreenSmall ? 'w-[600px]' : 'w-[960px]']">
      <section class="relative flex-grow overflow-y-auto pl-8 pt-6">
        <img
          src="@/assets/svg/close-black.svg"
          class="absolute right-0 top-0 mr-6 mt-8 h-6 w-6 cursor-pointer"
          alt="Close"
          data-test="close-templates-modal"
          @click="closeTemplatesModal"
        />
        <h2 class="font-serif text-2xl font-black text-copilot">Use a template</h2>
        <p class="mt-2 text-base text-shade-800">
          Quickly message candidates using pre-made templates or create your own.
        </p>
        <TemplateLibrary
          ref="templateLibrary"
          class="pr-8"
          @insert="selectTemplate"
          @send="sendTemplate"
        />
      </section>
    </div>
  </v-dialog>
</template>

<style>
.modal-content {
  @apply flex h-[674px] flex-col rounded-xl bg-white pb-2.5 shadow-menu;
}
</style>
